import { App } from "../data/statics/enums";
import { hosts } from "./hosts";

/** Endpoint Definitions (excluding domain) Organized By App
 *
 * Please note that the base urls are relative here. If the full url is needed
 * the `fullEndpoints` export should be used.
 */
export const endpoints = {
  [App.Core]: {
    auth: {
      base: "/api/v1/auth",

      // credential endpoints
      emailPasswordSignIn: "/sign-in",
      emailPasswordSignUp: "/sign-up",
      exchangeTempTokenPostMFA: "/ttexcmfa",
      confirmEmail: "/confirm-email",
      resendVerificationCode: "/resend-verification-code",
      forgotPassword: "/forgot-password",
      confirmForgotPassword: "/confirm-forgot-password",

      // federated endpoints
      federatedSignIn: "/federated/:provider",
      exchangeFederatedToken: "/exchange-federated-token",

      // data endpoints
      context: "/auth-context",
      workspace: "/verify-workspace",

      // refresh token endpoints
      refreshToken: "/r/refresh-token",
      forwardAuth: "/r/forward",
      signOut: "/r/sign-out",
    },
    oidcProxy: {
      base: "/oidc",
      config: "/.well-known/openid-configuration",
      authorize: "/authorize",
      token: "/token",
      callback: "/callback",
      jwks: "/jwks.json",
    },
    rsa: {
      base: "/rsa",
      publicKey: "/public-key",
    },
    mfa: {
      base: "/api/v1/mfa",
      verifySmsCode: "/verify-code/sign-in",
      verifyTotpCode: "/verify-totp-code",
    },
    service: {
      base: "/api/v1/service",

      exchangeToken: "/token/exchange",
      refreshToken: "/token/refresh",
      revokeRefreshToken: "/token/revokerefreshtoken",

      getUpdateUser: "/user",
      getUserPermissionsForAuthentication: "/user/permissions/authentication",
      getCognitoUser: "/user/cognito",
      reindexUser: "/user/reindex",

      addRemoveDeactivateUser: "/user/orgstatus",

      usersSearch: "/users/search",
      orgsSearch: "/orgs/search",
      fetchAllUserIdsWithStatusInOrg: "/users/ids/byorgandstatus",

      fetchUsersIdsForOrgAndPermissions: "/users/ids/bypermissions",

      resolveUsers: "/users/resolve",

      checkEmails: "/users/check-emails",

      updateRole: "/role/update",
      getRoles: "/roles",

      getCreateUpdateDeleteOrg: "/org",
      getOrgs: "/orgs",

      ensureSystemRolesAndPermissionsForApp:
        "/ensuresystemrolesandpermissionsforapp",
      getSystemRolesAndPermissionsForApp: "/getsystemrolesandpermissionsforapp",

      purgeDeletedPermissions: "/permissions/purge-deleted",

      getStatisticsInfo: "/statistics/info",
    },
  },

  [App.Account]: {
    user: {
      base: "/api/v1/user",
      profilePicture: "/profile-picture",
      name: "/name",
      company: "/company",
      info: "/info",
      logs: "/history",
      changeEmail: "/change-email",
      cancelPending: "/cancel-pending",
      confirmChangeEmail: "/confirm-change-email",
      originalPicture: "/original-picture",
      profilePictureData: "/profile-picture-data",
      changePassword: "/changePassword",
      confirmTerminateAccount: "/confirm-terminate-account",
      terminateAccount: "/terminate-account",
      clearMigration: "/migration",
    },
    mfa: {
      base: "/api/v1/mfa",
      generateTotpFactor: "/generate-totp-factor",
      confirmTotpFactor: "/confirm-totp-factor",
      deactivateTotpFactor: "/deactivate-totp-factor",
      confirmSmsFactor: "/confirm-sms-factor",
      deactivateSmsFactor: "/deactivate-sms-factor",
      sendSmsCode: "/send-sms-code",
    },
    application: {
      base: "/api/v1/application",
      getAppReferrer: "/app-referrer",
    },
  },

  [App.Admin]: {
    auth: {
      base: "/api/v1/auth",
      signOut: "/sign-out",
      refreshToken: "/r/refresh-token",
      impersonate: "/r/impersonate",
    },
    admin: {
      base: "/api/v1/admin",
      dashboard: "/dashboard",
      info: "/info",
      users: "/users",
      user: {
        //endpoints for retrieving or mutating a specific user
        selectedUser: "/selected-user",
        logs: "/logs",
        adminRemoveMFA: "/remove-mfa",
        adminModifyUserStatus: "/modify-user-status",
        adminTerminateUser: "/terminate-user",
        updateUserAttributes: "/update-user-attributes",
        adminUpdatePicture: "/update-profile-picture",
        adminRemovePicture: "/remove-profile-picture",
        originalPicture: "/original-picture",
        profilePictureData: "/profile-picture-data",
        clearUserMigration: "/clear-migration",
        migrateUser: "/migrate-user",
      },
      org: {
        searchOrgs: "/orgs",
        updateOrg: "/org",
      },
      upstreamProvider: {
        crudUpstreamProvider: "/upstream-provider",
        getUpstreamProviders: "/upstream-providers",
        updateUpstreamProviderSecret: "/upstream-provider/client-secret",
      },
    },
  },
} as const;

/** Prepare Full Endpoints
 *
 * This helper function prepares all endpoints for use within any app
 * by adding env specific hosts to generate full endpoint urls
 *
 * @returns - All fully formed endpoints separated by app
 */
function prepareFullEndpoints() {
  const result = Object.entries(endpoints).map(([id, e]) => {
    const _endpoints = { ...e }; // clone the object

    // Add the appropriate domain to all endpoints...
    Object.entries(_endpoints).forEach(([key, { base, ...rest }]) => {
      const _base = `${hosts.backend[id as App]}${base}`;
      const _rest = JSON.parse(
        JSON.stringify(rest)
          .replace(/:"/g, `:"${_base}`) // prepend base url
          .replace(/([^:])(\/\/+)/g, "$1/"), // remove double slashes
      );

      (_endpoints as { [key: string]: unknown })[key] = {
        base: _base,
        ..._rest,
      };
    });

    return [id, _endpoints] as const;
  });

  return Object.fromEntries(result) as typeof endpoints;
}

/** Endpoint Definitions (including domain) Organized By App
 *
 * Please note that these are full urls including the domain. If relative urls
 * are needed the `endpoints` export should be used.
 */
export const fullEndpoints = prepareFullEndpoints();
